/**
 * Group API – all group/groupMembers writes go through Cloud Functions (backend).
 * No direct client writes to groups or groupMembers; backend validates and syncs.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';

const functions = getFunctions(app);

function call(name, data) {
  const fn = httpsCallable(functions, name);
  return fn(data);
}

/**
 * Join a group (open or invite-by-email). Backend creates groupMembers and syncs user.approvedGroupIds.
 * @param {string} groupId
 * @param {string} [actorName]
 * @param {{ memberId?: string, flatNumber?: string }} [options] - for apartment-type groups
 */
export async function groupJoin(groupId, actorName = '', options = {}) {
  const result = await call('groupJoin', { groupId, actorName, ...options });
  return result.data;
}

/**
 * Leave a group. Backend removes groupMembers and syncs user.approvedGroupIds.
 * @param {string} groupId
 */
export async function groupLeave(groupId) {
  const result = await call('groupLeave', { groupId });
  return result.data;
}

/**
 * Approve a pending member. Caller must be group admin or system admin.
 */
export async function groupApproveMember(groupId, memberUserId) {
  const result = await call('groupApproveMember', { groupId, memberUserId });
  return result.data;
}

/**
 * Revoke approval of a member. Caller must be group admin or system admin.
 */
export async function groupRevokeMember(groupId, memberUserId) {
  const result = await call('groupRevokeMember', { groupId, memberUserId });
  return result.data;
}

/**
 * Create a group. Caller becomes admin.
 * @param {object} data - { name, shortCode?, type?, isOpen?, gameIds?, invitedEmails?, adminIds? }
 * @returns {Promise<{ ok: boolean, groupId: string }>}
 */
export async function groupCreate(data) {
  const result = await call('groupCreate', { data });
  return result.data;
}

/**
 * Update group. Caller must be group admin or system admin.
 * @param {string} groupId
 * @param {object} data - { name?, shortCode?, isOpen?, gameIds?, invitedEmails?, adminIds? }
 */
export async function groupUpdate(groupId, data) {
  const result = await call('groupUpdate', { groupId, data });
  return result.data;
}

/**
 * Add a member to the group (group admin). Backend creates groupMembers and syncs.
 * @param {string} groupId
 * @param {string} userId
 * @param {object} [options] - { memberId?, flatNumber? }
 */
export async function groupAddMember(groupId, userId, options = {}) {
  const result = await call('groupAddMember', { groupId, userId, options });
  return result.data;
}
